import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  companyQuery,
  getCompanyQueryData,
  setCompanyQueryData,
} from "#modules/company/infra/company.query";
import { completeSetup } from "#modules/company/infra/completeSetup.api";

export type UseCompleteOnboardingOptions = {
  onSuccess?: (company: Awaited<ReturnType<typeof completeSetup>>) => void | Promise<void>;
};

export const useCompleteOnboarding = ({ onSuccess }: UseCompleteOnboardingOptions = {}) => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation({
    mutationFn: completeSetup,
    onMutate: async () => {
      await queryClient.cancelQueries(companyQuery());

      const previousCompany = getCompanyQueryData(queryClient);
      setCompanyQueryData(queryClient, (company) =>
        company ? { ...company, onboardingExitedAt: new Date() } : company,
      );

      return { previousCompany };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        setCompanyQueryData(queryClient, context.previousCompany);
      }
    },
    onSuccess: async (company) => {
      setCompanyQueryData(queryClient, (cached) =>
        cached ? { ...cached, onboardingExitedAt: company.onboardingExitedAt } : cached,
      );

      await onSuccess?.(company);
    },
    onSettled: () => {
      void queryClient.invalidateQueries(companyQuery());
    },
  });

  return { completeOnboarding: mutateAsync, ...rest };
};
